/**
 * Pushes out-of-stock products to the end of collection grids by fetching all pages,
 * applying a stable in-stock-first sort, and re-rendering the appropriate slice (or
 * managing an infinite-scroll buffer). Degrades to Shopify order on fetch failure.
 */
(function collectionStockSort() {
  if (window.__collectionStockSortInitialized) return;
  window.__collectionStockSortInitialized = true;

  /**
   * @param {{ id: string, available: boolean }[]} items
   */
  function stableSortInStockFirst(items) {
    const inStock = items.filter((i) => i.available);
    const outStock = items.filter((i) => !i.available);
    return [...inStock, ...outStock];
  }

  /**
   * @param {HTMLElement} grid
   */
  function extractCardsFromGrid(grid) {
    return [...grid.querySelectorAll(':scope > li[data-product-id]')].map((li) => ({
      id: li.dataset.productId,
      available: li.dataset.available === 'true',
      html: li.outerHTML,
    }));
  }

  /**
   * @param {HTMLElement} grid
   */
  function sortListItemsInPlace(grid) {
    const items = [...grid.querySelectorAll(':scope > li[data-product-id]')].map((li) => ({
      id: li.dataset.productId,
      available: li.dataset.available === 'true',
      el: li,
    }));
    const sorted = stableSortInStockFirst(items);
    sorted.forEach((item) => grid.appendChild(item.el));
  }

  /**
   * @param {HTMLElement} grid
   * @param {string[]} htmlStrings
   */
  function replaceGridLis(grid, htmlStrings) {
    grid.querySelectorAll(':scope > li').forEach((li) => li.remove());
    if (!htmlStrings.length) return;
    const tpl = document.createElement('template');
    tpl.innerHTML = htmlStrings.join('');
    grid.append(...tpl.content.childNodes);
  }

  /**
   * @param {HTMLElement} grid
   * @param {string[]} htmlStrings
   */
  function appendFromHtml(grid, htmlStrings) {
    if (!htmlStrings.length) return;
    const tpl = document.createElement('template');
    tpl.innerHTML = htmlStrings.join('');
    grid.append(...tpl.content.childNodes);
  }

  /**
   * @param {number} pageNum
   */
  async function fetchPageHtml(pageNum) {
    const u = new URL(window.location.href);
    u.searchParams.set('page', String(pageNum));
    u.hash = '';
    const res = await fetch(u.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });
    if (!res.ok) throw new Error('fetch failed');
    return res.text();
  }

  /**
   * @param {string} html
   */
  function parseCardsFromHtml(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const g = doc.querySelector('[data-testid="product-grid"]');
    if (!g) return [];
    return extractCardsFromGrid(g);
  }

  /**
   * @param {HTMLElement} grid
   */
  function applySinglePageOrder(grid) {
    grid.querySelectorAll(':scope > li[data-product-id]').forEach((li) => {
      li.style.order = li.dataset.available === 'true' ? '0' : '1';
    });
    grid.classList.add('collection-stock-sort--done');
  }

  /**
   * @param {{ currentPage: number, totalPages: number, productsPerPage: number }} cfg
   * @param {HTMLElement} grid
   */
  async function runMultiPage(cfg, grid) {
    const { currentPage, totalPages, productsPerPage } = cfg;
    grid.classList.add('collection-stock-sort--in-progress');
    grid.classList.remove('collection-stock-sort--done');
    try {
      const pageCards = extractCardsFromGrid(grid);
      const pagesToFetch = [];
      for (let p = 1; p <= totalPages; p++) {
        if (p !== currentPage) pagesToFetch.push(p);
      }
      const results = await Promise.all(pagesToFetch.map((p) => fetchPageHtml(p).then(parseCardsFromHtml)));
      /** @type {Map<number, ReturnType<typeof extractCardsFromGrid>>} */
      const byPage = new Map();
      byPage.set(currentPage, pageCards);
      for (let i = 0; i < pagesToFetch.length; i++) {
        byPage.set(pagesToFetch[i], results[i]);
      }
      const flat = [];
      for (let p = 1; p <= totalPages; p++) {
        flat.push(...(byPage.get(p) || []));
      }
      const sorted = stableSortInStockFirst(flat);
      const start = (currentPage - 1) * productsPerPage;
      const slice = sorted.slice(start, start + productsPerPage);
      replaceGridLis(
        grid,
        slice.map((x) => x.html)
      );
    } catch {
      /* keep original order */
    } finally {
      grid.classList.remove('collection-stock-sort--in-progress');
      grid.classList.add('collection-stock-sort--done');
    }
  }

  /**
   * @returns {Promise<HTMLElement | null>}
   */
  async function waitForResultsList() {
    await customElements.whenDefined('results-list');
    for (let i = 0; i < 120; i++) {
      const el = document.querySelector('results-list');
      if (el?.infinityScrollObserver) return el;
      await new Promise((r) => requestAnimationFrame(r));
    }
    return document.querySelector('results-list');
  }

  /**
   * @param {{ totalPages: number, productsPerPage: number }} cfg
   * @param {HTMLElement} grid
   */
  async function runInfiniteScroll(cfg, grid) {
    const totalPages = Number(grid.dataset.lastPage) || cfg.totalPages;
    const perPage = cfg.productsPerPage;

    if (totalPages <= 1) {
      sortListItemsInPlace(grid);
      grid.classList.add('collection-stock-sort--done');
      return;
    }

    grid.classList.add('collection-stock-sort--in-progress');
    grid.classList.remove('collection-stock-sort--done');

    /** @type {ReturnType<typeof extractCardsFromGrid>} */
    let rawConcat = [];
    /** @type {{ id: string, available: boolean, html: string }[]} */
    let globalSorted = [];
    /** @type {Set<string>} */
    const displayedIds = new Set();
    let nextFetchPage = 2;

    /** @type {IntersectionObserver | null} */
    let scrollObserver = null;
    /** @type {(() => void) | null} */
    let removeScrollFallback = null;

    const teardown = () => {
      if (scrollObserver) {
        scrollObserver.disconnect();
        scrollObserver = null;
      }
      removeScrollFallback?.();
      removeScrollFallback = null;
    };

    const remainingNotDisplayed = () => globalSorted.filter((x) => !displayedIds.has(x.id));

    const appendBatch = () => {
      const batch = remainingNotDisplayed().slice(0, perPage);
      if (!batch.length) return false;
      appendFromHtml(
        grid,
        batch.map((x) => x.html)
      );
      batch.forEach((x) => displayedIds.add(x.id));
      return true;
    };

    const maybePrefetch = async () => {
      while (remainingNotDisplayed().length < 2 * perPage && nextFetchPage <= totalPages) {
        const pages = [nextFetchPage, nextFetchPage + 1].filter((p) => p <= totalPages);
        if (!pages.length) break;
        try {
          const results = await Promise.all(pages.map((p) => fetchPageHtml(p).then(parseCardsFromHtml)));
          for (const cards of results) {
            rawConcat.push(...cards);
          }
          globalSorted = stableSortInStockFirst(rawConcat);
          nextFetchPage = pages[pages.length - 1] + 1;
        } catch {
          break;
        }
      }
    };

    const onNeedMore = async () => {
      while (remainingNotDisplayed().length === 0 && nextFetchPage <= totalPages) {
        const prevPage = nextFetchPage;
        await maybePrefetch().catch(() => {});
        if (nextFetchPage === prevPage) break;
      }
      if (!remainingNotDisplayed().length && nextFetchPage > totalPages) {
        teardown();
        return;
      }
      appendBatch();
      await maybePrefetch().catch(() => {});
      if (!remainingNotDisplayed().length && nextFetchPage > totalPages) {
        teardown();
      }
    };

    try {
      sortListItemsInPlace(grid);
      const page1Cards = extractCardsFromGrid(grid);
      const prefetchRange = [2, 3].filter((p) => p <= totalPages);
      const prefetched = await Promise.all(prefetchRange.map((p) => fetchPageHtml(p).then(parseCardsFromHtml)));
      rawConcat = [...page1Cards];
      for (const cards of prefetched) {
        rawConcat.push(...cards);
      }
      globalSorted = stableSortInStockFirst(rawConcat);
      nextFetchPage = prefetchRange.length ? Math.max(...prefetchRange) + 1 : 2;

      replaceGridLis(
        grid,
        globalSorted.slice(0, perPage).map((x) => x.html)
      );
      globalSorted.slice(0, perPage).forEach((x) => displayedIds.add(x.id));

      await maybePrefetch().catch(() => {});

      const resultsList = await waitForResultsList();
      const sentinel = resultsList?.querySelector('[ref="viewMoreNext"]');
      const horizonObs = resultsList?.infinityScrollObserver;

      if (horizonObs && sentinel) {
        horizonObs.unobserve(sentinel);
      }

      const io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              onNeedMore();
            }
          }
        },
        { rootMargin: '200px' }
      );

      if (sentinel) {
        scrollObserver = io;
        io.observe(sentinel);
      } else {
        let ticking = false;
        const onWinScroll = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            const docEl = document.documentElement;
            const nearBottom = docEl.scrollHeight - window.innerHeight - window.scrollY < 900;
            if (nearBottom) onNeedMore();
          });
        };
        window.addEventListener('scroll', onWinScroll, { passive: true });
        removeScrollFallback = () => window.removeEventListener('scroll', onWinScroll);
      }
    } catch {
      /* keep DOM */
    } finally {
      grid.classList.remove('collection-stock-sort--in-progress');
      grid.classList.add('collection-stock-sort--done');
    }
  }

  /**
   * @param {{ totalPages: number, productsPerPage: number, currentPage: number, enableInfiniteScroll: boolean }} cfg
   */
  function run(cfg) {
    const grid = document.querySelector('[data-testid="product-grid"]');
    if (!grid) return;

    if (cfg.enableInfiniteScroll) {
      runInfiniteScroll(cfg, grid).catch(() => {});
      return;
    }

    if (cfg.totalPages <= 1) {
      applySinglePageOrder(grid);
      return;
    }

    runMultiPage(cfg, grid).catch(() => {});
  }

  const cfg = window.__collection;
  if (!cfg) return;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => run(cfg), { once: true });
  } else {
    run(cfg);
  }
})();
